import { ReportData, KPIData, SalesSource, AdSpendSource, TradeSales, JobData } from '../types/reportTypes';

/**
 * Standardizes lead source names for consistent matching across reports
 * @param sourceName - The original source name to standardize
 * @returns The standardized source name for matching
 */
export const standardizeSourceName = (sourceName: string): string => {
  const sourceMap: Record<string, string> = {
    // Ad spend sources to standardized names
    "contractor appointment": "contractors appointments",
    "google adwords": "google paid search",
    "google lsa": "google local service ads", 
    "web to lead form (roofing calculator)": "web to lead form",
    "facebook / meta ads": "facebook/meta ads",
    "facebook/ meta ads": "facebook/meta ads",
    "facebook/meta ads": "facebook/meta ads",
    
    // Sales sources to standardized names
    "contractors appointments": "contractors appointments",
    "google paid search": "google paid search",
    "google organic search": "google organic",
    "google organic": "google organic",
    "web to lead form": "web to lead form",
    "networx": "networx",
    "thumbtack": "thumbtack",
    "homebuddy": "homebuddy",
    "suburban md spring home show": "suburban md spring home show",
    "yard sign": "yard sign",
    "referral": "referral",
    "referral - word of mouth": "referral - word of mouth",
    "yelp": "yelp",
    "angi": "angi",
    "previous customer": "previous customer"
  };
  
  // Convert to lowercase for case-insensitive comparison
  const lowercaseSource = sourceName.toLowerCase();
  
  // Return the standardized name if it exists, otherwise return the original name
  return sourceMap[lowercaseSource] || lowercaseSource;
};

/**
 * Formats a source name for consistent display in the UI
 * @param sourceName - The source name to format
 * @returns Formatted display name
 */
export const formatSourceDisplayName = (sourceName: string): string => {
  // First, standardize the name to get a consistent base form
  const standardized = standardizeSourceName(sourceName);
  
  // Then apply proper capitalization and formatting for display
  const displayMap: Record<string, string> = {
    "contractors appointments": "Contractors Appointments",
    "google paid search": "Google Paid Search",
    "google organic": "Google Organic",
    "web to lead form": "Web to Lead Form",
    "networx": "Networx",
    "thumbtack": "Thumbtack",
    "homebuddy": "HomeBuddy",
    "suburban md spring home show": "Suburban MD Spring Home Show",
    "yard sign": "Yard Sign",
    "referral": "Referral", 
    "referral - word of mouth": "Referral - Word of Mouth",
    "yelp": "Yelp",
    "angi": "Angi",
    "facebook/meta ads": "Facebook / Meta Ads",
    "google local service ads": "Google Local Service Ads",
    "previous customer": "Previous Customer"
  };
  
  return displayMap[standardized] || sourceName;
};

/**
 * Gets the quarter (1-4) from a month (1-12)
 * @param month Month number (1-12)
 * @returns Quarter number (1-4)
 */
export const getQuarterFromMonth = (month: number): number => {
  return Math.ceil(month / 3);
};

/**
 * Gets the months in a quarter
 * @param quarter Quarter number (1-4)
 * @returns Array of month numbers (1-12)
 */
export const getMonthsInQuarter = (quarter: number): number[] => {
  const startMonth = (quarter - 1) * 3 + 1;
  return [startMonth, startMonth + 1, startMonth + 2];
};

/**
 * Aggregates KPI data objects
 * @param kpiDataArray Array of KPI data objects to aggregate
 * @returns Aggregated KPI data
 */
export const aggregateKPIData = (kpiDataArray: KPIData[]): KPIData => {
  // If there's no data, return empty KPI
  if (!kpiDataArray || kpiDataArray.length === 0) {
    return {
      adSpend: 0,
      totalSales: 0,
      grossProfit: 0,
      salesCommissions: 0,
      managerFees: 0,
      companyOH: 0,
      netProfit: 0,
      netProfitPercent: 0,
      grossProfitPercent: 0
    };
  }

  // Sum all numeric KPI values
  const aggregated = kpiDataArray.reduce((acc, kpi) => {
    return {
      adSpend: acc.adSpend + (kpi.adSpend || 0),
      totalSales: acc.totalSales + (kpi.totalSales || 0),
      grossProfit: acc.grossProfit + (kpi.grossProfit || 0),
      salesCommissions: acc.salesCommissions + (kpi.salesCommissions || 0),
      managerFees: acc.managerFees + (kpi.managerFees || 0),
      companyOH: acc.companyOH + (kpi.companyOH || 0),
      netProfit: acc.netProfit + (kpi.netProfit || 0),
      netProfitPercent: 0, // Will calculate after summing
      grossProfitPercent: 0 // Will calculate after summing
    };
  }, {
    adSpend: 0,
    totalSales: 0,
    grossProfit: 0,
    salesCommissions: 0,
    managerFees: 0,
    companyOH: 0,
    netProfit: 0,
    netProfitPercent: 0,
    grossProfitPercent: 0
  });

  // Calculate percentages based on aggregated totals
  if (aggregated.totalSales > 0) {
    aggregated.netProfitPercent = aggregated.netProfit / aggregated.totalSales;
    aggregated.grossProfitPercent = aggregated.grossProfit / aggregated.totalSales;
  }

  return aggregated;
};

/**
 * Merges arrays of sales source data, combining entries with the same source
 * @param salesSourceArrays Arrays of sales source data to merge
 * @returns Merged sales source data
 */
export const mergeSalesSources = (salesSourceArrays: SalesSource[][]): SalesSource[] => {
  // Create a map to store merged data by source
  const sourceMap = new Map<string, SalesSource>();

  // Flatten and combine all arrays
  salesSourceArrays.flat().forEach(source => {
    const existing = sourceMap.get(source.source);
    
    if (existing) {
      // Combine with existing entry
      sourceMap.set(source.source, {
        source: source.source,
        totalSales: (existing.totalSales || 0) + (source.totalSales || 0),
        count: (existing.count || 0) + (source.count || 0),
        grossProfit: (existing.grossProfit || 0) + (source.grossProfit || 0)
      });
    } else {
      // New entry
      sourceMap.set(source.source, { ...source });
    }
  });

  // Convert map back to array
  return Array.from(sourceMap.values());
};

/**
 * Merges arrays of ad spend source data, combining entries with the same source
 * @param adSpendSourceArrays Arrays of ad spend source data to merge
 * @returns Merged ad spend source data
 */
export const mergeAdSpendSources = (adSpendSourceArrays: AdSpendSource[][]): AdSpendSource[] => {
  // Create a map to store merged data by source
  const sourceMap = new Map<string, AdSpendSource>();

  // Flatten and combine all arrays
  adSpendSourceArrays.flat().forEach(source => {
    const existing = sourceMap.get(source.source);
    
    if (existing) {
      // Combine with existing entry
      sourceMap.set(source.source, {
        source: source.source,
        spend: (existing.spend || 0) + (source.spend || 0)
      });
    } else {
      // New entry
      sourceMap.set(source.source, { ...source });
    }
  });

  // Convert map back to array
  return Array.from(sourceMap.values());
};

/**
 * Merges arrays of trade sales data, combining entries with the same trade
 * @param tradeSalesArrays Arrays of trade sales data to merge
 * @returns Merged trade sales data
 */
export const mergeTradeSales = (tradeSalesArrays: TradeSales[][]): TradeSales[] => {
  // Create a map to store merged data by trade
  const tradeMap = new Map<string, TradeSales>();

  // Flatten and combine all arrays
  tradeSalesArrays.flat().forEach(trade => {
    const existing = tradeMap.get(trade.trade);
    
    if (existing) {
      // Combine with existing entry
      tradeMap.set(trade.trade, {
        trade: trade.trade,
        totalSales: (existing.totalSales || 0) + (trade.totalSales || 0),
        count: (existing.count || 0) + (trade.count || 0),
        grossProfit: (existing.grossProfit || 0) + (trade.grossProfit || 0)
      });
    } else {
      // New entry
      tradeMap.set(trade.trade, { ...trade });
    }
  });

  // Convert map back to array
  return Array.from(tradeMap.values());
};

/**
 * Combine multiple reports' data into a single report
 * @param reports Array of report data objects to combine
 * @param title Optional title for the combined report
 * @returns Combined report data
 */
export const combineReports = (
  reports: ReportData[],
  title = 'Combined Report'
): ReportData => {
  // If there are no reports, return empty report structure
  if (!reports || reports.length === 0) {
    return {
      kpis: {
        adSpend: 0,
        totalSales: 0,
        grossProfit: 0,
        salesCommissions: 0,
        managerFees: 0,
        companyOH: 0,
        netProfit: 0,
        netProfitPercent: 0,
        grossProfitPercent: 0
      },
      adSpendBySource: [],
      salesBySource: [],
      salesByTrade: [],
      jobData: [],
      period: undefined
    };
  }

  // Combine KPI data
  const kpis = aggregateKPIData(reports.map(report => report.kpis));

  // Combine sales by source
  const salesBySource = mergeSalesSources(reports.map(report => report.salesBySource || []));

  // Combine ad spend by source
  const adSpendBySource = mergeAdSpendSources(reports.map(report => report.adSpendBySource || []));

  // Combine sales by trade
  const salesByTrade = mergeTradeSales(reports.map(report => report.salesByTrade || []));

  // Combine job data (just concatenate arrays)
  const jobData = reports.flatMap(report => report.jobData || []);

  // Determine the period from the reports being combined
  // For combined reports, we'll use a custom period representation
  let period;
  const firstMonth = Math.min(...reports.filter(r => r.period?.month).map(r => r.period!.month));
  const lastMonth = Math.max(...reports.filter(r => r.period?.month).map(r => r.period!.month));

  // If we have a consistent year, use it
  const years = Array.from(new Set(reports.filter(r => r.period?.year).map(r => r.period!.year)));
  if (years.length === 1) {
    period = {
      month: firstMonth, // Use the first month as the representative month
      year: years[0]
    };
  }

  return {
    kpis,
    adSpendBySource,
    salesBySource,
    salesByTrade,
    jobData,
    period
  };
};

/**
 * Create a quarterly report by aggregating monthly reports
 * @param monthlyReports Array of monthly report data for the quarter
 * @param quarter Quarter number (1-4)
 * @param year Year
 * @returns Quarterly report data
 */
export const createQuarterlyReport = (
  monthlyReports: ReportData[],
  quarter: number,
  year: number
): ReportData => {
  // Filter reports to only include those from the specified quarter
  const quarterMonths = getMonthsInQuarter(quarter);
  
  const reportsInQuarter = monthlyReports.filter(report => 
    report.period && 
    report.period.month && 
    report.period.year === year && 
    quarterMonths.includes(report.period.month)
  );

  // Combine the reports
  const combinedData = combineReports(reportsInQuarter);

  // Override period information for quarterly report
  // Keep month property to satisfy TypeScript, using the first month of the quarter
  const firstMonthOfQuarter = ((quarter - 1) * 3) + 1;
  return {
    ...combinedData,
    period: {
      month: firstMonthOfQuarter,
      year,
      quarter // additional property that won't cause type errors
    }
  };
};

/**
 * Create a YTD (Year-to-Date) report by aggregating monthly reports
 * @param monthlyReports Array of monthly report data
 * @param year Year
 * @param throughMonth Last month to include (1-12)
 * @param fiscal Whether to use fiscal year (Oct-Sep) instead of calendar year
 * @returns YTD report data
 */
export const createYTDReport = (
  monthlyReports: ReportData[],
  year: number,
  throughMonth: number = new Date().getMonth() + 1,
  fiscal = false
): ReportData => {
  let startMonth = 1; // Jan for calendar year
  let startYear = year;
  let endMonth = throughMonth;
  let endYear = year;

  if (fiscal) {
    // Fiscal year typically runs Oct-Sep
    startMonth = 10; // Oct
    startYear = year - 1;
    endMonth = throughMonth > 9 ? throughMonth : throughMonth + 12; // Adjust for spanning years
    endYear = throughMonth > 9 ? year : year - 1;
  }

  // Filter reports to only include those from the start through the specified month
  const reportsInRange = monthlyReports.filter(report => {
    if (!report.period || !report.period.month || !report.period.year) {
      return false;
    }

    const reportMonth = report.period.month;
    const reportYear = report.period.year;

    if (fiscal) {
      // For fiscal year, we need to handle the year transition
      if (reportYear === startYear && reportMonth >= startMonth) {
        return true;
      }
      if (reportYear === endYear && reportMonth <= (endMonth % 12 || 12)) {
        return true;
      }
      return false;
    } else {
      // For calendar year, simpler check
      return reportYear === year && reportMonth <= throughMonth;
    }
  });

  // Combine the reports
  const combinedData = combineReports(reportsInRange);

  // Override period information for YTD report
  return {
    ...combinedData,
    period: {
      month: throughMonth, // Use the throughMonth as the representative month
      year,
      ytd: true, // additional properties won't cause type errors when used elsewhere
      throughMonth,
      fiscal
    }
  };
};

/**
 * Calculate period-over-period differences between two reports
 * @param currentPeriod Current period report data
 * @param previousPeriod Previous period report data
 * @returns Report with difference values and percentage changes
 */
export const calculatePeriodComparison = (
  currentPeriod: ReportData,
  previousPeriod: ReportData
): any => {
  // Initialize comparison object
  const comparison: any = {
    kpis: {},
    salesBySource: [],
    adSpendBySource: [],
    salesByTrade: []
  };

  // Compare KPIs
  for (const key in currentPeriod.kpis) {
    if (Object.prototype.hasOwnProperty.call(currentPeriod.kpis, key)) {
      const currentValue = (currentPeriod.kpis as any)[key] || 0;
      const previousValue = (previousPeriod.kpis as any)[key] || 0;
      const difference = currentValue - previousValue;
      const percentChange = previousValue !== 0 
        ? (difference / Math.abs(previousValue)) * 100 
        : (currentValue === 0 ? 0 : 100); // If both 0, then 0% change, otherwise 100% increase

      comparison.kpis[key] = {
        current: currentValue,
        previous: previousValue,
        difference,
        percentChange
      };
    }
  }

  // Compare sales by source
  const allSources = [...new Set([
    ...(currentPeriod.salesBySource || []).map(s => s.source),
    ...(previousPeriod.salesBySource || []).map(s => s.source)
  ])];

  comparison.salesBySource = allSources.map(source => {
    const currentSource = (currentPeriod.salesBySource || []).find(s => s.source === source);
    const previousSource = (previousPeriod.salesBySource || []).find(s => s.source === source);
    
    return {
      source,
      totalSales: {
        current: currentSource?.totalSales || 0,
        previous: previousSource?.totalSales || 0,
        difference: (currentSource?.totalSales || 0) - (previousSource?.totalSales || 0),
        percentChange: previousSource?.totalSales 
          ? (((currentSource?.totalSales || 0) - (previousSource?.totalSales || 0)) / Math.abs(previousSource.totalSales)) * 100 
          : ((currentSource?.totalSales || 0) === 0 ? 0 : 100)
      },
      count: {
        current: currentSource?.count || 0,
        previous: previousSource?.count || 0,
        difference: (currentSource?.count || 0) - (previousSource?.count || 0),
        percentChange: previousSource?.count 
          ? (((currentSource?.count || 0) - (previousSource?.count || 0)) / Math.abs(previousSource.count)) * 100 
          : ((currentSource?.count || 0) === 0 ? 0 : 100)
      },
      grossProfit: {
        current: currentSource?.grossProfit || 0,
        previous: previousSource?.grossProfit || 0,
        difference: (currentSource?.grossProfit || 0) - (previousSource?.grossProfit || 0),
        percentChange: previousSource?.grossProfit 
          ? (((currentSource?.grossProfit || 0) - (previousSource?.grossProfit || 0)) / Math.abs(previousSource.grossProfit)) * 100 
          : ((currentSource?.grossProfit || 0) === 0 ? 0 : 100)
      }
    };
  });

  // Compare ad spend by source
  const allAdSources = [...new Set([
    ...(currentPeriod.adSpendBySource || []).map(s => s.source),
    ...(previousPeriod.adSpendBySource || []).map(s => s.source)
  ])];

  comparison.adSpendBySource = allAdSources.map(source => {
    const currentSource = (currentPeriod.adSpendBySource || []).find(s => s.source === source);
    const previousSource = (previousPeriod.adSpendBySource || []).find(s => s.source === source);
    
    return {
      source,
      spend: {
        current: currentSource?.spend || 0,
        previous: previousSource?.spend || 0,
        difference: (currentSource?.spend || 0) - (previousSource?.spend || 0),
        percentChange: previousSource?.spend 
          ? (((currentSource?.spend || 0) - (previousSource?.spend || 0)) / Math.abs(previousSource.spend)) * 100 
          : ((currentSource?.spend || 0) === 0 ? 0 : 100)
      }
    };
  });

  // Compare sales by trade
  const allTrades = [...new Set([
    ...(currentPeriod.salesByTrade || []).map(t => t.trade),
    ...(previousPeriod.salesByTrade || []).map(t => t.trade)
  ])];

  comparison.salesByTrade = allTrades.map(trade => {
    const currentTrade = (currentPeriod.salesByTrade || []).find(t => t.trade === trade);
    const previousTrade = (previousPeriod.salesByTrade || []).find(t => t.trade === trade);
    
    return {
      trade,
      totalSales: {
        current: currentTrade?.totalSales || 0,
        previous: previousTrade?.totalSales || 0,
        difference: (currentTrade?.totalSales || 0) - (previousTrade?.totalSales || 0),
        percentChange: previousTrade?.totalSales 
          ? (((currentTrade?.totalSales || 0) - (previousTrade?.totalSales || 0)) / Math.abs(previousTrade.totalSales)) * 100 
          : ((currentTrade?.totalSales || 0) === 0 ? 0 : 100)
      },
      count: {
        current: currentTrade?.count || 0,
        previous: previousTrade?.count || 0,
        difference: (currentTrade?.count || 0) - (previousTrade?.count || 0),
        percentChange: previousTrade?.count 
          ? (((currentTrade?.count || 0) - (previousTrade?.count || 0)) / Math.abs(previousTrade.count)) * 100 
          : ((currentTrade?.count || 0) === 0 ? 0 : 100)
      },
      grossProfit: {
        current: currentTrade?.grossProfit || 0,
        previous: previousTrade?.grossProfit || 0,
        difference: (currentTrade?.grossProfit || 0) - (previousTrade?.grossProfit || 0),
        percentChange: previousTrade?.grossProfit 
          ? (((currentTrade?.grossProfit || 0) - (previousTrade?.grossProfit || 0)) / Math.abs(previousTrade.grossProfit)) * 100 
          : ((currentTrade?.grossProfit || 0) === 0 ? 0 : 100)
      }
    };
  });

  // Add period information
  comparison.periods = {
    current: currentPeriod.period,
    previous: previousPeriod.period
  };

  return comparison;
};

export default {
  getQuarterFromMonth,
  getMonthsInQuarter,
  aggregateKPIData,
  mergeSalesSources,
  mergeAdSpendSources,
  mergeTradeSales,
  combineReports,
  createQuarterlyReport,
  createYTDReport,
  calculatePeriodComparison
};